import { useFridgeInformation } from "@jidlomat/terminal-core/hooks/useFridgeInformation";
import { useAppSelector } from "@jidlomat/terminal-core/hooks/utilsHooks/storeHooks";
import { selectFilters, selectSelectedLanguage } from "@jidlomat/terminal-core/slices/appStateSlice";
import { selectTracksStock } from "@jidlomat/terminal-core/slices/fridgeInfoSlice";
import type { LocalDishType } from "@jidlomat/terminal-core/types/mainTypes";
import { getDishTypeTextValue, parseInstructions } from "@jidlomat/terminal-core/utils/utils";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import parse from "html-react-parser";
import { useTranslation } from "react-i18next";
import { AllergensMapping } from "../../../../utils/AllergensMapping";
import { parseFridgeSegments } from "../../../../utils/parseFridgeSegments";
import { AllergenChip } from "../AllergenChip";
import { DishTypeFridgePositionMap } from "../DishTypeFridgePositionMap";

const StyledDescriptionSection = styled(Box)(() => ({
	width: "60%",
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
}));

export const DescriptionTab = ({ dishType }: { dishType: LocalDishType }) => {
	const selectedLanguage = useAppSelector(selectSelectedLanguage);
	const { allergens } = useAppSelector(selectFilters);
	// Where the dish is and how many are left: a fridge without readers knows neither.
	const tracksStock = useAppSelector(selectTracksStock);
	const { t } = useTranslation();

	const { segments } = useFridgeInformation();

	const parsedSegments = parseFridgeSegments(segments, dishType);

	// We have toi temporarily split the composition saved in DB as
	// it also contains the cooking instructions
	const description = getDishTypeTextValue(dishType, selectedLanguage, "description");
	const parsedDescription = parseInstructions(description);
	// Flag against the active filters ONLY — the single source of truth the meal list and the fridge
	// lighting also read. The customer's saved preferences are merged into the filters at order start
	// (userPreferencesMiddleware), so they are already covered here; reading them separately would
	// re-flag a dish the customer has explicitly unchecked, contradicting the list and the spotlight.
	const dishTypeIncludesFilteredAllergen = dishType.allergens?.some((allergen) => allergens.includes(allergen));

	return (
		<Box display="flex" justifyContent="space-between" paddingTop={2}>
			{tracksStock && (
				<Box width="30%">
					<Typography variant="headlineMedium">{t("dishTypeDetail.descriptionTab.location")}</Typography>
					<Typography>
						{t("dishTypeDetail.descriptionTab.piecesLeft" as never, {
							count: dishType.dishes?.length,
						})}
					</Typography>
					<DishTypeFridgePositionMap parsedSegments={parsedSegments} />
				</Box>
			)}
			<StyledDescriptionSection>
				<Box>{parse(Array.isArray(parsedDescription) ? parsedDescription[0] : parsedDescription)}</Box>
				<Box display="flex" gap={1} flexWrap="wrap">
					<Typography variant="headlineMedium" width="100%">
						{t("dishTypeDetail.descriptionTab.allergens")}
					</Typography>
					{AllergensMapping.filter((allergen) => dishType.allergens?.includes(allergen.name)).map((allergen) => (
						<AllergenChip
							allergen={allergen}
							sx={({ palette }) => ({
								...(allergens.includes(allergen.name) && {
									backgroundColor: palette.error.light,
									svg: {
										"& path": {
											fill: palette.common.white,
										},
									},
								}),
							})}
							key={`dishDetail_allergenList_${allergen.name}`}
						/>
					))}
					{dishTypeIncludesFilteredAllergen && (
						<Typography color="error.main" width="100%">
							{t("dishTypeDetail.descriptionTab.filteredAllergensIncluded")}
						</Typography>
					)}
				</Box>
			</StyledDescriptionSection>
		</Box>
	);
};
